// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import {
  ExchangeOrder,
  ExchangeOrderFilter,
  ExchangeOrderStatus,
  exchangeOrderSchema,
  statusToAPI,
} from '@/exchange-order/api/types';
import queryString from 'query-string';
import { z } from 'zod';

/**
 * Type definition for the paginated response.
 */
interface PagedExchangeOrdersResponse {
  results: unknown[];
  totalCount: number;
}

/**
 * Type definition for the API response wrapper.
 */
interface ApiResponse {
  data: PagedExchangeOrdersResponse;
  metadata: { requestId: string };
}

/**
 * Type definition for the get exchange orders API response.
 */
interface ExchangeOrdersResponse {
  exchangeOrders: ExchangeOrder[];
  resultCount: number;
}

/**
 * Fetches exchange orders with the specified filter criteria.
 */
export async function getExchangeOrders(
  filter?: ExchangeOrderFilter
): Promise<ExchangeOrdersResponse> {
  const url = '/v1/exchange-group';
  const response = await api.get<ApiResponse>(url, {
    paramsSerializer: (parameters) => queryString.stringify(parameters),
    params: {
      statuses:
        filter?.status && filter.status !== 'all'
          ? [statusToAPI(filter.status as ExchangeOrderStatus)]
          : undefined,
      search: filter?.searchTerm,
      page: filter?.page,
      limit: filter?.pageSize,
    },
  });

  // Parse and validate each exchange order with Zod
  const exchangeOrdersSchema = z.array(exchangeOrderSchema);
  const exchangeOrders = await parseResponse(
    exchangeOrdersSchema,
    response.data.data.results,
    {
      schemaName: 'ExchangeOrders',
      endpoint: url,
    }
  );

  return {
    exchangeOrders,
    resultCount: response.data.data.totalCount,
  };
}
